import React, { useMemo } from 'react'
import { useAppTheme, Box, Flex, Loading, Select, Heading } from '@workwhile/ui'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { useAdminReviewStats } from 'queries/review/useAdminReviewStats'
import { ReviewType } from 'api/review'
import { FILTER_CHIP_DATE_FORMAT } from 'constants/dateFormats'
import { format, subDays } from 'date-fns'
import { DateRangePickerFilter } from 'components/Filter/DateRangePickerFilter'
import { SelectFilter } from 'components/Filter/SelectFilter'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { dateRangeBoundsSchema } from 'lib/dateRangeBoundsSchema'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

const REVIEW_TYPES: { value: ReviewType; label: string }[] = [
  { value: 'far_clock_out', label: 'Far Clock Out' },
  { value: 'long_outage', label: 'Long Outage' },
  { value: 'too_long_outside_geofence', label: 'Too Long Outside Geofence' },
  { value: 'device_sharing', label: 'Device Sharing' },
  { value: 'no_trip', label: 'No Trip' },
  { value: 'supervisor_marked_no_show', label: 'Supervisor Marked No Show' },
  {
    value: 'zero_earnings_finished_work',
    label: 'Zero Earnings Finished Work',
  },
  { value: 'location_mocked', label: 'Location Mocked' },
  { value: 'worker_claims_they_worked', label: 'Worker Claims They Worked' },
  { value: 'suspicious_past_shift', label: 'Suspicious Past Shift' },
  { value: 'invalid_mealbreak', label: 'Invalid Mealbreak' },
  { value: 'overlapping_work', label: 'Overlapping Work' },
]

const chartFiltersSchema = z.object({
  dateRange: dateRangeBoundsSchema,
  reviewTypes: z.array(z.string()).default([]),
})

const reviewTypeOptions = REVIEW_TYPES.map((rt) => ({
  label: rt.label,
  value: rt.value,
}))

type ChartFiltersSchema = z.infer<typeof chartFiltersSchema>

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  const count = payload[0]?.value || 0

  // Get the full date from the payload data
  const dataPoint = payload[0]?.payload
  let dateWithYear = label
  if (dataPoint?.fullDate) {
    try {
      const parsedDate = new Date(dataPoint.fullDate)
      if (!isNaN(parsedDate.getTime())) {
        dateWithYear = format(parsedDate, FILTER_CHIP_DATE_FORMAT)
      }
    } catch {
      // If parsing fails, try to parse the label
      try {
        const parsedDate = new Date(label)
        if (!isNaN(parsedDate.getTime())) {
          dateWithYear = format(parsedDate, FILTER_CHIP_DATE_FORMAT)
        }
      } catch {
        // Use label as is
      }
    }
  } else {
    // Fallback: try to parse the label
    try {
      const parsedDate = new Date(label)
      if (!isNaN(parsedDate.getTime())) {
        dateWithYear = format(parsedDate, FILTER_CHIP_DATE_FORMAT)
      }
    } catch {
      // Use label as is
    }
  }

  return (
    <Box
      bg="white"
      p={2}
      border="1px solid"
      borderColor="neutrals.100"
      borderRadius="md"
      boxShadow="md"
    >
      <Box fontWeight="bold" mb={1}>
        {dateWithYear}
      </Box>
      <Box color="text" style={{ color: '#000' }}>
        Count: {count}
      </Box>
    </Box>
  )
}

export const AdminReviewStatsChart = () => {
  const { colors } = useAppTheme()

  const defaultDateRange = {
    from: subDays(new Date(), 30),
    to: new Date(),
  }

  const methods = useForm<ChartFiltersSchema>({
    resolver: zodResolver(chartFiltersSchema),
    defaultValues: {
      dateRange: {
        from: defaultDateRange.from,
        to: defaultDateRange.to,
      },
      reviewTypes: [],
    },
  })

  const dateRange = methods.watch('dateRange')
  const selectedReviewTypes = methods.watch('reviewTypes') as ReviewType[]

  const { data, isLoading } = useAdminReviewStats({
    startDate: dateRange?.from || defaultDateRange.from,
    endDate: dateRange?.to || defaultDateRange.to,
    reviewTypes:
      selectedReviewTypes.length > 0 ? selectedReviewTypes : undefined,
  })

  // Transform data for chart: backend already aggregates by date
  const chartData = useMemo(() => {
    if (!data?.items || data.items.length === 0) return []

    return data.items.map((item) => {
      const formattedDate = format(new Date(item.date), 'MMM d')
      return {
        date: formattedDate,
        fullDate: item.date, // Store full date for tooltip
        count: item.count,
      }
    })
  }, [data])

  return (
    <Box width="100%">
      <Heading level={4} mb={3}>
        Admin Review Count By Date
      </Heading>
      <FormProvider {...methods}>
        <Flex flexDirection="column" gap={3}>
          {/* Filters Row */}
          <Flex
            flexDirection="row"
            gap={2}
            flexWrap="wrap"
            alignItems="flex-start"
          >
            <DateRangePickerFilter<ChartFiltersSchema>
              dateRangeFieldPath="dateRange"
              label="Time Range"
              filterKey="time-range"
              placeholder="Select Date range"
              resetDefaultValues={{
                dateRange: {
                  from: defaultDateRange.from,
                  to: defaultDateRange.to,
                },
              }}
            />

            {/* Review Type Filters */}
            <SelectFilter<ChartFiltersSchema, string>
              fieldPath="reviewTypes"
              label="Review Types"
              filterKey="review-types"
              options={reviewTypeOptions}
              formatSelectedAttributes={(values) => {
                return values.map(
                  (value) =>
                    REVIEW_TYPES.find((rt) => rt.value === value)?.label ||
                    value
                )
              }}
            >
              <Box>
                <Controller
                  name="reviewTypes"
                  control={methods.control}
                  render={({ field }) => (
                    <Select
                      isMulti
                      isSearchable
                      isClearable
                      options={reviewTypeOptions}
                      value={reviewTypeOptions.filter((option) =>
                        (field.value || []).includes(option.value)
                      )}
                      onChange={(selected) => {
                        field.onChange(
                          selected ? selected.map((item) => item.value) : []
                        )
                      }}
                      placeholder="Select review types..."
                    />
                  )}
                />
              </Box>
            </SelectFilter>
          </Flex>

          {/* Chart */}
          <Box position="relative" width="100%" height={300}>
            {isLoading ? (
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="rgba(255, 255, 255, 0.8)"
                zIndex={10}
              >
                <Loading />
              </Box>
            ) : chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="count"
                    name="Count"
                    fill={colors.primaries[200] || colors.primary200}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
              >
                No data available for the selected filters
              </Box>
            )}
          </Box>
        </Flex>
      </FormProvider>
    </Box>
  )
}
